"use client"
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useCallback, useReducer, useRef } from 'react';
import { useCashFlowProjectionsData } from '../../hooks/useCashFlowProjectionsData';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { createRating, getRatingUsage } from '../../api';
import { useProjectedCashFlows } from '../../components/cashFlowProjections/useProjectedCashFlows';
import {
    projectionInputsInitialState,
    projectionInputsReducer,
    ratingInitialState,
    ratingReducer,
    usageInitialState,
    usageReducer,
} from './stockAnalysis/reducers';
import { ensureScreenshotCaptureStyles } from './stockAnalysis/screenshotUtils';
import StockAnalysisRatingPanel from './stockAnalysis/StockAnalysisRatingPanel';
import StockAnalysisHistoryPanel from './stockAnalysis/StockAnalysisHistoryPanel';

const StockAnalysisDcfPanel = lazy(() => import('./stockAnalysis/StockAnalysisDcfPanel'));

ensureScreenshotCaptureStyles();

const loadingMessages = [
    'Reading the annual report...',
    'Checking the income statement...',
    'Analyzing free cash flow trends...',
    'Measuring the competitive moat...',
    'Evaluating management quality...',
    'Calculating intrinsic value...',
    'Channeling Warren Buffett...',
    'Looking for margin of safety...',
    'Reviewing the balance sheet...',
    'Almost there...',
];

function computeGrowthRates(history) {
    return history.map((entry, i, arr) => {
        if (i === 0) return null;
        const curr = entry.operatingCashFlows - entry.capitalExpenditures;
        const prev = arr[i - 1].operatingCashFlows - arr[i - 1].capitalExpenditures;
        if (prev === 0) return null;
        return ((curr - prev) / Math.abs(prev)) * 100;
    });
}

function avgLastN(growthRates, n) {
    const slice = growthRates.slice(-n).filter((x) => x !== null);
    if (!slice.length) return null;
    return slice.reduce((sum, v) => sum + v, 0) / slice.length;
}

function StockAnalysis() {
    const { ticker } = useParams();
    const normalizedTicker = ticker ? ticker.toUpperCase() : '';
    const [{ initialFCF, outstandingShares, growthRate, discountRate }, dispatchProjectionInputs] = useReducer(
        projectionInputsReducer,
        projectionInputsInitialState
    );
    const {
        loading,
        error,
        operatingCashFlows,
        initialFreeCashFlow,
        outstandingShares: fetchedOutstandingShares,
    } = useCashFlowProjectionsData(ticker);
    const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
    const [
        {
            result: ratingResult,
            isLoading: isRatingLoading,
            error: ratingError,
            loadingMessageIndex,
            loadingProgress,
        },
        dispatchRating,
    ] = useReducer(ratingReducer, ratingInitialState);
    const [{ data: usage, isLoading: isUsageLoading, error: usageError }, dispatchUsage] = useReducer(
        usageReducer,
        usageInitialState
    );
    const loadingProgressRef = useRef(loadingProgress);

    useEffect(() => {
        loadingProgressRef.current = loadingProgress;
    }, [loadingProgress]);

    useEffect(() => {
        if (!isRatingLoading) return undefined;

        const messageInterval = setInterval(() => {
            dispatchRating({ type: 'tick_message', messageCount: loadingMessages.length });
        }, 2000);

        const progressInterval = setInterval(() => {
            dispatchRating({ type: 'tick_progress' });
        }, 800);

        return () => {
            clearInterval(messageInterval);
            clearInterval(progressInterval);
        };
    }, [isRatingLoading]);

    useEffect(() => {
        if (isRatingLoading || loadingProgressRef.current <= 0) return undefined;

        dispatchRating({ type: 'complete_progress' });
        const resetTimeout = setTimeout(() => {
            dispatchRating({ type: 'reset_progress' });
        }, 500);

        return () => clearTimeout(resetTimeout);
    }, [isRatingLoading]);

    const { cashFlows, totalValue } = useProjectedCashFlows(initialFCF, growthRate, discountRate);

    const handleGenerateRating = async () => {
        if (!normalizedTicker) return;
        dispatchRating({ type: 'start' });
        try {
            const token = await getAccessTokenSilently();
            const response = await createRating(normalizedTicker, token);
            if (!response?.success || !response?.rating) {
                throw new Error('Could not generate rating.');
            }
            dispatchRating({ type: 'success', payload: response.rating });
            toast.success('AI rating generated!');
            await refreshUsage();
        } catch (err) {
            console.error('Failed to generate rating', err);
            const message = err?.response?.data?.message || err.message || 'Unable to generate rating.';
            dispatchRating({ type: 'error', payload: message });
            toast.error('Unable to generate AI rating at the moment.');
        }
    };

    const history = operatingCashFlows
        .filter((entry) => entry.year <= new Date().getFullYear() - 1)
        .sort((a, b) => a.year - b.year);

    const growthRates = computeGrowthRates(history);
    const avg3 = avgLastN(growthRates, 3);
    const avg6 = avgLastN(growthRates, 6);
    const avg10 = avgLastN(growthRates, 10);

    const refreshUsage = useCallback(async () => {
        if (!isAuthenticated) {
            dispatchUsage({ type: 'clear' });
            return;
        }
        dispatchUsage({ type: 'load_start' });
        try {
            const token = await getAccessTokenSilently();
            const data = await getRatingUsage(token);
            dispatchUsage({ type: 'load_success', payload: data });
        } catch (err) {
            console.error('Failed to load rating usage', err);
            dispatchUsage({ type: 'load_error', payload: 'Unable to load rating usage right now.' });
        }
    }, [getAccessTokenSilently, isAuthenticated]);

    useEffect(() => {
        if (!isAuthLoading) {
            refreshUsage();
        }
    }, [refreshUsage, isAuthLoading]);

    useEffect(() => {
        dispatchProjectionInputs({
            type: 'sync_from_sec_data',
            initialFreeCashFlow,
            outstandingShares: fetchedOutstandingShares,
        });
    }, [ticker, initialFreeCashFlow, fetchedOutstandingShares]);

    const isPremiumMember = usage?.is_premium === true;
    const usedRatings = usage?.used_this_month ?? 0;
    const monthlyLimit = isPremiumMember ? null : usage?.monthly_limit ?? 3;
    const canGenerateRating = usage?.can_generate !== false;
    const renewalDate = usage?.subscription_renewal_date ?? null;
    const renewalLabel = renewalDate
        ? new Date(renewalDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null;
    const usageLabel = usage
        ? isPremiumMember
            ? `Premium member — unlimited ratings${renewalLabel ? ` • Renews ${renewalLabel}` : ''}`
            : `${usedRatings}/${monthlyLimit} ratings used this month`
        : isUsageLoading
            ? 'Checking usage...'
            : 'Usage data unavailable';
    const ratingButtonDisabled = isRatingLoading || (usage && usage.can_generate === false);

    return (
        <Box w="100%">
            <Box display="flex" flexDirection="column" w="100%" pr={{ base: 0, lg: 2 }}>
                <Flex
                    justifyContent="space-around"
                    align={{ base: 'flex-start', lg: 'center' }}
                    gap={{ base: 8, lg: 5 }}
                    direction={{ base: 'column', lg: 'row' }}
                    w="100%"
                >
                    <Flex
                        flexDirection="column"
                        align={{ base: 'stretch', lg: 'center' }}
                        width={{ base: '100%', lg: '45%' }}
                        minW={0}
                    >
                        <StockAnalysisHistoryPanel
                            loading={loading}
                            error={error}
                            history={history}
                            growthRates={growthRates}
                            avg3={avg3}
                            avg6={avg6}
                            avg10={avg10}
                            normalizedTicker={normalizedTicker}
                            initialFCF={initialFCF}
                            onInitialFCFChange={(value) => dispatchProjectionInputs({ type: 'set_initial_fcf', value })}
                            cashFlows={cashFlows}
                            onApplyGrowthRate={(value) => dispatchProjectionInputs({ type: 'set_growth_rate', value })}
                        />
                    </Flex>

                    <Flex
                        flexDirection="column"
                        justifyContent="flex-start"
                        width={{ base: '100%', lg: '50%' }}
                        minW={0}
                    >
                        <StockAnalysisRatingPanel
                            normalizedTicker={normalizedTicker}
                            authState={{ authenticated: isAuthenticated }}
                            usageState={{
                                data: usage,
                                loading: isUsageLoading,
                                error: usageError,
                                label: usageLabel,
                                usedRatings,
                                monthlyLimit,
                                premiumMember: isPremiumMember,
                                renewalDate,
                                canGenerate: canGenerateRating,
                            }}
                            ratingState={{
                                result: ratingResult,
                                loading: isRatingLoading,
                                error: ratingError,
                                buttonDisabled: ratingButtonDisabled,
                            }}
                            loadingState={{
                                messageIndex: loadingMessageIndex,
                                progress: loadingProgress,
                                messages: loadingMessages,
                            }}
                            onGenerateRating={handleGenerateRating}
                        />

                        <Suspense
                            fallback={(
                                <Box py={8} display="flex" justifyContent="center">
                                    <Spinner size="lg" color="green.500" />
                                </Box>
                            )}
                        >
                            <StockAnalysisDcfPanel
                                totalValue={totalValue}
                                outstandingShares={outstandingShares}
                                onOutstandingSharesChange={(value) => dispatchProjectionInputs({ type: 'set_outstanding_shares', value })}
                                growthRate={growthRate}
                                onGrowthRateChange={(value) => dispatchProjectionInputs({ type: 'set_growth_rate', value })}
                                discountRate={discountRate}
                                onDiscountRateChange={(value) => dispatchProjectionInputs({ type: 'set_discount_rate', value })}
                                cashFlows={cashFlows}
                            />
                        </Suspense>
                    </Flex>
                </Flex>
            </Box>
        </Box>
    );
}

export default StockAnalysis;
